<script setup lang="ts">
  import { ref, onMounted, watch, computed, getCurrentInstance } from 'vue';
  import { useIntersectionObserver } from '@vueuse/core';
  import StatsService from '@/api/services/stats.service';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IEntityStats } from '@/interfaces/IEntityStats';
  import { IItem } from '@/interfaces/IItem';
  import { Globals } from '@/modules/Globals';
  import { OrderMode } from '@/enums/OrderEnums';
  import { IBusinessDayAnchor } from '@/domain/stats/business-day';
  import {
    TDashboardTimeframe,
    getDashboardRange,
    isHourlyTimeframe,
  } from '@/domain/dashboard/dashboard-timeframes';

  const props = defineProps<{
    /** Branch business day resolved by the dashboard page (null = pending). */
    businessDay: IBusinessDayAnchor | null;
  }>();

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  const restaurantStore = useRestaurantStore();
  const uploadsPath = Globals.uploadsPath;

  // Services
  const statsService = new StatsService();

  // State
  const loading = ref(true);
  const hasLoadedOnce = ref(false);
  const containerRef = ref<HTMLElement>();
  const isInView = ref(false);
  const selectedTimeframe = ref<TDashboardTimeframe>('7days');
  const selectedMetric = ref<'quantity' | 'revenue'>('quantity');
  // Latest load wins: slower stale responses never overwrite newer data.
  let loadSeq = 0;
  const topItems = ref<any[]>([]);
  const bottomItems = ref<any[]>([]);
  const showEmptyState = ref(false);
  const showWhatsAppNotSupported = ref(false);
  const isExpanded = ref(false);
  const allItemsWithData = ref<any[]>([]);

  // Computed
  const timeframeOptions = computed(() => [
    { text: String(proxy.$t('dashboard.tfToday')), value: 'today' },
    { text: String(proxy.$t('dashboard.tfYesterday')), value: 'yesterday' },
    { text: String(proxy.$t('dashboard.tfLast7')), value: '7days' },
    { text: String(proxy.$t('dashboard.tfLast4w')), value: '4weeks' },
    { text: String(proxy.$t('dashboard.tfThisMonth')), value: 'thisMonth' },
  ]);

  const maxValue = computed(() => {
    // Determine maximum value among all items; ensure at least 1 to avoid division by zero
    const values = allItemsWithData.value.map((i) => i.value);
    const max = values.length ? Math.max(...values) : 0;
    return max > 0 ? max : 1;
  });

  // Set up intersection observer
  const { stop } = useIntersectionObserver(
    containerRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !hasLoadedOnce.value) {
        isInView.value = true;
        loadItemsData();
        stop(); // Stop observing once loaded
      }
    },
    {
      threshold: 0.1, // Trigger when 10% of the element is visible
    }
  );

  // Methods
  const processItemsData = (stats: IEntityStats<IItem>) => {
    if (!stats.groups || stats.groups.length === 0) {
      showEmptyState.value = true;
      return;
    }

    // Get the appropriate group based on selected metric
    const groupIndex = selectedMetric.value === 'quantity' ? 0 : 1; // 0: sales count, 1: revenue
    const selectedGroup = stats.groups[groupIndex];

    if (!selectedGroup || !selectedGroup.stats) {
      showEmptyState.value = true;
      return;
    }

    // Debug the raw stats
    console.log('Raw stats from API:', stats);
    console.log('Selected group stats:', selectedGroup.stats);

    // Convert to array with item details and sort
    const itemsWithData = selectedGroup.stats
      .map((stat) => ({
        id: stat.id,
        name:
          stats.entities[stat.id]?.name ||
          String(proxy.$t('v2Stats.seriesReport.deletedEntity')),
        picture: stats.entities[stat.id]?.picture || null,
        value: stat.value,
        // For revenue, also get quantity from sales group
        quantity:
          selectedMetric.value === 'revenue' && stats.groups[0]?.stats
            ? stats.groups[0].stats.find((s) => s.id === stat.id)?.value || 0
            : stat.value,
      }))
      .sort((a, b) => b.value - a.value);

    if (itemsWithData.length === 0) {
      showEmptyState.value = true;
      topItems.value = [];
      bottomItems.value = [];
      allItemsWithData.value = [];
      return;
    }

    showEmptyState.value = false;

    // Store all items for expand/collapse functionality
    allItemsWithData.value = itemsWithData;

    // Get top and bottom items based on expanded state
    const itemCount = isExpanded.value ? 10 : 5;
    topItems.value = itemsWithData.slice(0, itemCount);

    // For bottom items, only show if we have more than the current item count
    if (itemsWithData.length > itemCount) {
      // Get the last items, but in ascending order (worst first)
      bottomItems.value = itemsWithData.slice(-itemCount).reverse();
    } else {
      bottomItems.value = [];
    }

    // Debug logging
    console.log('Total items with sales:', itemsWithData.length);
    console.log('Top items:', topItems.value);
    console.log('Bottom items:', bottomItems.value);
  };

  const loadItemsData = async () => {
    if (!isInView.value) return; // Don't load if not in view
    const anchor = props.businessDay;
    // The page is still resolving the business day; its watcher loads later.
    if (!anchor) return;
    const seq = ++loadSeq;

    // Check if WhatsApp mode (for the anchor's branch; reset on every load)
    const branch = restaurantStore.branches?.find(
      (b) => b.id === anchor.branchId
    );
    showWhatsAppNotSupported.value = branch?.ordersMode === OrderMode.WhatsApp;
    if (showWhatsAppNotSupported.value) {
      loading.value = false;
      hasLoadedOnce.value = true;
      return;
    }

    loading.value = true;
    hasLoadedOnce.value = true;

    try {
      const timeframe = selectedTimeframe.value;
      const range = getDashboardRange(timeframe, anchor.today);
      const stats = await statsService.getProductsStats(
        anchor.restaurantId,
        anchor.branchId,
        range.from,
        isHourlyTimeframe(timeframe) ? undefined : range.to
      );
      // A newer load started while this one was in flight.
      if (seq !== loadSeq) return;

      processItemsData(stats);
    } catch (error) {
      if (seq !== loadSeq) return;
      console.error('Error loading product stats:', error);
      showEmptyState.value = true;
      topItems.value = [];
      bottomItems.value = [];
    } finally {
      if (seq === loadSeq) loading.value = false;
    }
  };

  const formatValue = (value: number) => {
    if (selectedMetric.value === 'revenue') {
      return `${restaurantStore.currency?.symbol || '$'}${value.toFixed(0)}`;
    }
    return value.toString();
  };

  // Local implementation of thumb function
  const thumb = (filename: string) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return `${parts[0]}_thumb.${parts[1]}`;
  };

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;

    // Re-process items with new count
    const itemCount = isExpanded.value ? 10 : 5;

    if (allItemsWithData.value.length > 0) {
      topItems.value = allItemsWithData.value.slice(0, itemCount);

      if (allItemsWithData.value.length > itemCount) {
        bottomItems.value = allItemsWithData.value.slice(-itemCount).reverse();
      } else {
        bottomItems.value = [];
      }
    }
  };

  // Watchers (a new business day = branch switch or page re-anchor)
  watch([selectedMetric, selectedTimeframe, () => props.businessDay], () => {
    if (isInView.value) {
      isExpanded.value = false; // Reset expansion when filters change
      loadItemsData();
    }
  });

  // Lifecycle
  onMounted(() => {
    // Data loading is triggered by intersection observer
  });
</script>

<template>
  <v-card class="top-bottom-items-card" elevation="0" ref="containerRef">
    <v-card-title class="card-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon">mdi-trophy</v-icon>
          <span class="header-title">{{ $t('dashboard.topBottomTitle') }}</span>
        </div>
        <div class="header-right">
          <v-btn
            text
            small
            :to="`/${restaurantStore.restaurant?.stringId}/stats/products`"
            class="view-more-btn"
          >
            {{ $t('dashboard.viewMore') }}
            <v-icon small right>mdi-chevron-right</v-icon>
          </v-btn>
          <v-select
            v-model="selectedTimeframe"
            :items="timeframeOptions"
            dense
            outlined
            hide-details
            class="timeframe-select"
          />
        </div>
      </div>
    </v-card-title>

    <v-card-text class="card-content">
      <!-- Show skeleton while not loaded -->
      <div v-if="!hasLoadedOnce" class="skeleton-container">
        <div class="items-grid">
          <div class="items-column">
            <v-skeleton-loader type="heading" class="skeleton-heading" />
            <v-skeleton-loader
              v-for="i in 5"
              :key="`top-skeleton-${i}`"
              type="list-item-avatar"
              class="skeleton-item"
            />
          </div>
          <div class="items-column">
            <v-skeleton-loader type="heading" class="skeleton-heading" />
            <v-skeleton-loader
              v-for="i in 5"
              :key="`bottom-skeleton-${i}`"
              type="list-item-avatar"
              class="skeleton-item"
            />
          </div>
        </div>
      </div>

      <!-- Actual content - shown after first load -->
      <div v-else>
        <!-- WhatsApp not supported message -->
        <div v-if="showWhatsAppNotSupported" class="empty-state">
          <v-icon size="64" color="grey lighten-2">mdi-whatsapp</v-icon>
          <p class="empty-state-text">
            {{ $t('v2Stats.waModeText') }}
          </p>
        </div>

        <!-- Empty state -->
        <div v-else-if="showEmptyState" class="empty-state">
          <v-icon size="64" color="grey lighten-2">mdi-package-variant</v-icon>
          <p class="empty-state-text">
            {{ $t('dashboard.noProductData') }}
          </p>
        </div>

        <!-- Content -->
        <div v-else class="content-wrapper">
          <!-- Metric toggle -->
          <div class="metric-toggle-wrapper">
            <v-btn-toggle
              v-model="selectedMetric"
              mandatory
              dense
              class="metric-toggle"
            >
              <v-btn value="quantity" small>
                <v-icon left small>mdi-pound</v-icon>
                {{ $t('dashboard.metricQuantity') }}
              </v-btn>
              <v-btn value="revenue" small>
                <v-icon left small>mdi-currency-usd</v-icon>
                {{ $t('dashboard.metricRevenue') }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Items grid -->
          <div class="items-grid">
            <!-- Top sellers column -->
            <div class="items-column">
              <div class="column-header top-sellers">
                <v-icon>mdi-fire</v-icon>
                <span>{{ $t('dashboard.topSellers') }}</span>
              </div>

              <template v-if="loading">
                <v-skeleton-loader
                  v-for="i in 5"
                  :key="`top-loading-${i}`"
                  type="list-item-avatar"
                  class="skeleton-item"
                />
              </template>

              <div v-else class="items-list">
                <div
                  v-for="(item, index) in topItems"
                  :key="`top-${item.id}`"
                  class="item-row"
                >
                  <div class="rank-badge top-rank">{{ index + 1 }}</div>

                  <div v-if="item.picture" class="item-image">
                    <v-img
                      :src="`${uploadsPath}/${thumb(item.picture)}`"
                      :alt="item.name"
                      :aspect-ratio="1"
                      cover
                    >
                      <template v-slot:placeholder>
                        <v-skeleton-loader type="image" />
                      </template>
                    </v-img>
                  </div>
                  <div v-else class="item-image item-image-placeholder">
                    <v-icon>mdi-food</v-icon>
                  </div>

                  <div class="item-details">
                    <div class="item-name">{{ item.name }}</div>
                    <div
                      v-if="selectedMetric === 'revenue'"
                      class="item-subtitle"
                    >
                      {{ $t('dashboard.unitsCount', { count: item.quantity }) }}
                    </div>
                  </div>

                  <div class="item-value">
                    <div class="value-text">{{ formatValue(item.value) }}</div>
                    <div class="value-bar">
                      <div
                        class="value-fill"
                        :style="{
                          width: `${
                            maxValue ? (item.value / maxValue) * 100 : 0
                          }%`,
                        }"
                      ></div>
                    </div>
                  </div>
                </div>

                <!-- Expand/Collapse button for top sellers -->
                <div
                  v-if="!loading && allItemsWithData.length > 10"
                  class="expand-button-wrapper"
                >
                  <v-btn
                    text
                    small
                    @click="toggleExpanded"
                    class="expand-button"
                  >
                    <v-icon left small>{{
                      isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
                    }}</v-icon>
                    {{
                      isExpanded
                        ? $t('dashboard.collapse')
                        : $t('dashboard.expand')
                    }}
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Bottom sellers column - always show -->
            <div class="items-column">
              <div class="column-header bottom-sellers">
                <v-icon>mdi-trending-down</v-icon>
                <span>{{ $t('dashboard.bottomSellers') }}</span>
              </div>

              <template v-if="loading">
                <v-skeleton-loader
                  v-for="i in 5"
                  :key="`bottom-loading-${i}`"
                  type="list-item-avatar"
                  class="skeleton-item"
                />
              </template>

              <div
                v-else-if="bottomItems.length === 0"
                class="no-items-message"
              >
                <v-icon color="grey lighten-2">mdi-information-outline</v-icon>
                <p>
                  {{
                    topItems.length <= 5
                      ? $t('dashboard.notEnoughProducts')
                      : $t('dashboard.noDataAvailable')
                  }}
                </p>
              </div>

              <div v-else class="items-list">
                <div
                  v-for="(item, index) in bottomItems"
                  :key="`bottom-${item.id}`"
                  class="item-row"
                >
                  <div class="rank-badge bottom-rank">{{ index + 1 }}</div>

                  <div v-if="item.picture" class="item-image">
                    <v-img
                      :src="`${uploadsPath}/${thumb(item.picture)}`"
                      :alt="item.name"
                      :aspect-ratio="1"
                      cover
                    >
                      <template v-slot:placeholder>
                        <v-skeleton-loader type="image" />
                      </template>
                    </v-img>
                  </div>
                  <div v-else class="item-image item-image-placeholder">
                    <v-icon>mdi-food</v-icon>
                  </div>

                  <div class="item-details">
                    <div class="item-name">{{ item.name }}</div>
                    <div
                      v-if="selectedMetric === 'revenue'"
                      class="item-subtitle"
                    >
                      {{ $t('dashboard.unitsCount', { count: item.quantity }) }}
                    </div>
                  </div>

                  <div class="item-value">
                    <div class="value-text">{{ formatValue(item.value) }}</div>
                    <div class="value-bar">
                      <div
                        class="value-fill bottom-value-fill"
                        :style="{
                          width: `${
                            maxValue ? (item.value / maxValue) * 100 : 0
                          }%`,
                        }"
                      ></div>
                    </div>
                  </div>
                </div>

                <!-- Expand/Collapse button for bottom sellers -->
                <div
                  v-if="!loading && allItemsWithData.length > 10"
                  class="expand-button-wrapper"
                >
                  <v-btn
                    text
                    small
                    @click="toggleExpanded"
                    class="expand-button"
                  >
                    <v-icon left small>{{
                      isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
                    }}</v-icon>
                    {{
                      isExpanded
                        ? $t('dashboard.collapse')
                        : $t('dashboard.expand')
                    }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .top-bottom-items-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;
    @apply tw-w-full tw-overflow-hidden;

    .card-header {
      @apply tw-pb-2 tw-pt-3;

      .header-content {
        @apply tw-flex tw-items-center tw-justify-between tw-w-full;
        @apply tw-flex-wrap tw-gap-2;

        .header-left {
          @apply tw-flex tw-items-center tw-gap-2;

          .header-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
          }

          .header-title {
            @apply tw-text-base tw-font-semibold tw-text-neutral-800;
          }
        }

        .header-right {
          @apply tw-flex tw-items-center tw-gap-2;

          .view-more-btn {
            @apply tw-text-orange-600;
            @apply tw-whitespace-nowrap;
          }

          .timeframe-select {
            @apply tw-max-w-xs;

            ::v-deep .v-input__control {
              min-height: 36px;
            }

            ::v-deep .v-select__selection {
              @apply tw-text-sm;
            }
          }
        }
      }
    }

    .card-content {
      @apply tw-pt-0;
    }

    .skeleton-container {
      .skeleton-heading {
        @apply tw-mb-3;
      }

      .skeleton-item {
        @apply tw-mb-2;
        ::v-deep .v-skeleton-loader__list-item-avatar {
          height: 48px;
        }
      }
    }

    .empty-state {
      @apply tw-text-center tw-py-12;

      .empty-state-text {
        @apply tw-text-neutral-600 tw-font-medium tw-mt-4;
      }
    }

    .content-wrapper {
      .metric-toggle-wrapper {
        @apply tw-flex tw-justify-center tw-mb-4;

        .metric-toggle {
          @apply tw-bg-neutral-100 tw-rounded;
          @apply tw-text-xs;

          ::v-deep .v-btn {
            @apply tw-h-8;
            letter-spacing: normal;
          }
        }
      }
    }

    .items-grid {
      @apply tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4;

      .items-column {
        @apply tw-bg-neutral-50 tw-rounded-lg tw-p-3;
        @apply tw-min-h-0;

        .column-header {
          @apply tw-flex tw-items-center tw-gap-1 tw-mb-3;
          @apply tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide;

          &.top-sellers {
            @apply tw-text-orange-600;

            .v-icon {
              @apply tw-text-orange-500;
              font-size: 18px;
            }
          }

          &.bottom-sellers {
            @apply tw-text-neutral-600;

            .v-icon {
              @apply tw-text-neutral-500;
              font-size: 18px;
            }
          }
        }

        .items-list {
          @apply tw-space-y-2;

          .expand-button-wrapper {
            @apply tw-flex tw-justify-center tw-mt-3;

            .expand-button {
              @apply tw-text-orange-600;

              ::v-deep .v-btn__content {
                @apply tw-flex tw-items-center;
              }
            }
          }
        }

        .no-items-message {
          @apply tw-text-center tw-py-6;
          @apply tw-text-neutral-500;

          .v-icon {
            @apply tw-mb-2;
            font-size: 32px;
          }

          p {
            @apply tw-text-xs;
          }
        }

        .item-row {
          @apply tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1;
          @apply tw-rounded-md tw-transition-all tw-duration-200;
          @apply tw-bg-white hover:tw-shadow-sm;
          @apply tw-border tw-border-transparent hover:tw-border-neutral-200;

          .rank-badge {
            @apply tw-w-6 tw-h-6 tw-rounded-full;
            @apply tw-flex tw-items-center tw-justify-center;
            @apply tw-font-bold tw-text-xs tw-flex-shrink-0;

            &.top-rank {
              @apply tw-bg-gradient-to-br tw-from-orange-400 tw-to-orange-500;
              @apply tw-text-white tw-shadow-sm;
            }

            &.bottom-rank {
              @apply tw-bg-neutral-200 tw-text-neutral-600;
            }
          }

          .item-image {
            @apply tw-w-10 tw-h-10 tw-rounded tw-object-cover;
            @apply tw-flex-shrink-0 tw-bg-neutral-100;
            @apply tw-overflow-hidden;

            // Force square aspect ratio and prevent flex override
            ::v-deep .v-image {
              @apply tw-w-full tw-h-full;
              flex: none !important;
            }

            ::v-deep img {
              @apply tw-w-full tw-h-full tw-object-cover;
            }

            &.item-image-placeholder {
              @apply tw-bg-neutral-100;
              @apply tw-flex tw-items-center tw-justify-center;

              .v-icon {
                @apply tw-text-neutral-400;
                font-size: 20px;
              }
            }
          }

          .item-details {
            @apply tw-flex-1 tw-min-w-0;

            .item-name {
              @apply tw-text-sm tw-font-medium tw-text-neutral-800;
              @apply tw-truncate tw-leading-tight;
            }

            .item-subtitle {
              @apply tw-text-xs tw-text-neutral-500 tw-mt-0.5;
            }
          }

          .item-value {
            @apply tw-text-right tw-flex-shrink-0;

            .value-text {
              @apply tw-text-sm tw-font-bold tw-text-neutral-800;
              @apply tw-tabular-nums;
            }

            .value-bar {
              @apply tw-h-0.5 tw-bg-neutral-200 tw-rounded-full tw-mt-1;
              @apply tw-w-14;

              .value-fill {
                @apply tw-h-full tw-rounded-full tw-transition-all tw-duration-300;
                @apply tw-bg-gradient-to-r tw-from-orange-400 tw-to-orange-500;

                &.bottom-value-fill {
                  @apply tw-bg-gradient-to-r tw-from-neutral-300 tw-to-neutral-400;
                }
              }
            }
          }
        }
      }
    }
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .top-bottom-items-card {
      .card-header {
        .header-content {
          .header-left {
            @apply tw-w-full tw-mb-2;
          }

          .header-right {
            @apply tw-w-full;
            @apply tw-flex tw-flex-col tw-gap-2;

            .view-more-btn {
              @apply tw-w-full tw-justify-center;
            }

            .timeframe-select {
              @apply tw-max-w-none tw-w-full;
            }
          }
        }
      }

      .items-grid {
        @apply tw-gap-3;

        .items-column {
          @apply tw-p-2;

          .column-header {
            @apply tw-text-xs;
            @apply tw-mb-2;
          }

          .item-row {
            @apply tw-p-1.5;

            .rank-badge {
              @apply tw-w-5 tw-h-5;
              @apply tw-text-xs;
            }

            .item-image {
              @apply tw-w-8 tw-h-8;
            }

            .item-details {
              .item-name {
                @apply tw-text-xs;
              }
            }

            .item-value {
              .value-text {
                @apply tw-text-xs;
              }

              .value-bar {
                @apply tw-w-12;
              }
            }
          }

          .items-list {
            .expand-button-wrapper {
              @apply tw-mt-2;

              .expand-button {
                @apply tw-text-xs;
              }
            }
          }
        }
      }
    }
  }

  // Ensure images maintain aspect ratio
  ::v-deep .v-responsive__content {
    @apply tw-flex tw-items-center tw-justify-center;
  }

  // Dark mode support (if needed)
  @media (prefers-color-scheme: dark) {
    .top-bottom-items-card {
      .items-column {
        @apply tw-bg-neutral-800;

        .item-row {
          @apply tw-bg-neutral-900;
          @apply hover:tw-border-neutral-700;
        }
      }
    }
  }
</style>
